import { useCallback, useMemo } from 'react';

import { Cart, useShowCart } from '@givebacks/api/store';
import { useShopStorage } from '@givebacks/shop';

type UseIsFeeInCartArgs = {
  studentFeeId: string;
  causeId: string;
};

const getFeeInCart = (studentFeeId: string, cartData: Cart) => {
  return cartData?.cart_items.find((item) => item.student_fee_id === studentFeeId);
};

/**
 * This hook is used to check if a student fee is in the cart.
 * It returns the cartId, cartItemId, and the total amount of the fee in the cart.
 */
const useIsFeeInCart = ({ studentFeeId, causeId }: UseIsFeeInCartArgs) => {
  const { getCartIdByStoreId } = useShopStorage();
  const cartId = getCartIdByStoreId(causeId);

  const { data: feeInCart } = useShowCart({
    cartId,
    causeId: causeId,
    select: useCallback((data: Cart) => getFeeInCart(studentFeeId, data), [studentFeeId]),
  });

  const isStudentFeeInCart = useMemo(() => {
    return feeInCart !== undefined;
  }, [feeInCart]);

  const cartItemId = useMemo(() => {
    return feeInCart?.uuid;
  }, [feeInCart]);

  return {
    isStudentFeeInCart,
    feeTotal: feeInCart?.total,
    cartId,
    cartItemId,
  };
};

export { useIsFeeInCart };
